import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { Clock } from "lucide-react";

import { Badge } from "@/components/ui/badge/badge";
import { getTodayBreakStatusQueryOptions } from "@/config/queries/emp-query-options";
import { BreakList } from "@/components/break/break-list";
import { formatDuration } from "@/components/break/format";
import { formatTimeToIST } from "@/utils/timezone";

type Status = {
  label: string;
  variant?: "default" | "secondary";
  className?: string;
};

export const TodayCard = () => {
  const key = Cookies.get("key") as string;
  const { data } = useQuery(getTodayBreakStatusQueryOptions(key));

  if (!data) return null;

  const status: Status = data.onBreak
    ? { label: "On a break", className: "bg-amber-500 text-black hover:bg-amber-500" }
    : data.checkedIn
    ? { label: "Working", className: "bg-green-600 hover:bg-green-600" }
    : { label: "Not checked in", variant: "secondary" };

  return (
    <div
      className="w-full md:max-w-md rounded-md bg-card flex flex-col gap-2 shadow-sm border p-4 mb-4"
      data-testid="today-card"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium flex items-center gap-2">
          <Clock className="w-4 h-4" /> Today
        </h2>
        <Badge variant={status.variant} className={status.className} data-testid="today-status">
          {status.label}
        </Badge>
      </div>

      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="text-muted-foreground">Checked in at</div>
        <div className="font-medium" data-testid="today-checkin-time">
          {data.checkinAt ? formatTimeToIST(data.checkinAt) : "—"}
        </div>
        <div className="text-muted-foreground">Total break time</div>
        <div className="font-medium" data-testid="today-total-break">
          {formatDuration(data.totalBreakSeconds)}
        </div>
      </div>

      <div className="mt-1">
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
          Breaks today ({data.breakCount})
        </div>
        <BreakList breaks={data.breaks} emptyText="No breaks yet today." />
      </div>
    </div>
  );
};
